import { useFormik } from "formik";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import { Link, useHistory, withRouter } from "react-router-dom";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
} from "reactstrap";
import { object, string } from "yup";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import {
  useArrondissementsByCity,
  useCities,
  useCountries,
  useCreateQuartier,
  useProvinces,
} from "../../../hooks/useGeography";

const AddQuartier = (props) => {
  const history = useHistory();
  const createQuartierMutation = useCreateQuartier();

  document.title = props.t("Ajout d'un quartier - Le Conseiller du BTP");

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      name: "",
      country: "",
      province: "",
      city: "",
      arrondissement: "",
    },

    validationSchema: object().shape({
      name: string().required(props.t("Veuillez entrer le nom du quartier")),
      country: string().required(props.t("Veuillez sélectionner un pays")),
      province: string().required(props.t("Veuillez sélectionner une province")),
      city: string().required(props.t("Veuillez sélectionner une ville")),
      arrondissement: string().required(
        props.t("Veuillez sélectionner un arrondissement")
      ),
    }),

    onSubmit: async (values) => {
      createQuartierMutation.mutate(
        {
          name: values.name,
          arrondissementId: values.arrondissement,
        },
        {
          onSuccess: () => {
            history.push("/geolocalisation-quartier");
          },
        }
      );
    },
  });

  const countriesQ = useCountries();
  const provincesQ = useProvinces(validation.values.country);
  const citiesQ = useCities(validation.values.province);
  const arrondissementsQ = useArrondissementsByCity(validation.values.city);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={props.t("Ajout d'un quartier")}
          pageTitle={props.t("Quartier")}
        />

        <Row>
          <Col lg={12}>
            <Form
              encType="multipart/form-data"
              onSubmit={(e) => {
                e.preventDefault();
                validation.handleSubmit();
              }}
            >
              <Card>
                <CardBody>
                    <div className="mb-3">
                      <Label className="form-label" htmlFor="country">
                        {props.t("Pays")}
                      </Label>
                      <Input
                        type="select"
                        className="form"
                        name="country"
                        value={validation.values.country}
                        onBlur={validation.handleBlur}
                        onChange={(event) => {
                          validation.setFieldValue("country", event.target.value);
                          validation.setFieldValue("province", "");
                          validation.setFieldValue("city", "");
                          validation.setFieldValue("arrondissement", "");
                        }}
                        required
                      >
                        <option value="">Sélectionner le pays concerné</option>
                        {(countriesQ.data || []).map((country) => (
                          <option key={country.id} value={country.id}>
                            {country.name}
                          </option>
                        ))}
                      </Input>
                      {validation.errors.country && validation.touched.country ? (
                        <FormFeedback type="invalid">
                          {validation.errors.country}
                        </FormFeedback>
                      ) : null}
                    </div>
                    <div className="mb-3">
                      <Label className="form-label" htmlFor="province">
                        {props.t("Province")}
                      </Label>
                      <Input
                        type="select"
                        className="form"
                        name="province"
                        value={validation.values.province}
                        onBlur={validation.handleBlur}
                        onChange={(event) => {
                          validation.setFieldValue("province", event.target.value);
                          validation.setFieldValue("city", "");
                          validation.setFieldValue("arrondissement", "");
                        }}
                        disabled={!validation.values.country || provincesQ.isLoading}
                        required
                      >
                        <option value="">Sélectionner la province concernée</option>
                        {(provincesQ.data || []).map((province) => (
                          <option key={province.id} value={province.id}>
                            {province.name}
                          </option>
                        ))}
                      </Input>
                      {validation.errors.province && validation.touched.province ? (
                        <FormFeedback type="invalid">
                          {validation.errors.province}
                        </FormFeedback>
                      ) : null}
                    </div>
                    <div className="mb-3">
                      <Label className="form-label" htmlFor="city">
                        {props.t("Ville")}
                      </Label>
                      <Input
                        type="select"
                        className="form"
                        name="city"
                        value={validation.values.city}
                        onBlur={validation.handleBlur}
                        onChange={(event) => {
                          validation.setFieldValue("city", event.target.value);
                          validation.setFieldValue("arrondissement", "");
                        }}
                        disabled={!validation.values.province || citiesQ.isLoading}
                        required
                      >
                        <option value="">Sélectionner la ville concernée</option>
                        {(citiesQ.data || []).map((city) => (
                          <option key={city.id} value={city.id}>
                            {city.name}
                          </option>
                        ))}
                      </Input>
                      {validation.errors.city && validation.touched.city ? (
                        <FormFeedback type="invalid">
                          {validation.errors.city}
                        </FormFeedback>
                      ) : null}
                    </div>
                  <div className="mb-3">
                    <Label className="form-label" htmlFor="arrondissement">
                        {props.t("Arrondissement")}
                    </Label>
                    <Input
                      type="select"
                      className="form"
                      name="arrondissement"
                      value={validation.values.arrondissement}
                      onBlur={validation.handleBlur}
                      onChange={validation.handleChange}
                        disabled={!validation.values.city || arrondissementsQ.isLoading}
                      required
                    >
                        <option value="">Sélectionner l'arrondissement concerné</option>
                        {(arrondissementsQ.data || []).map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                    </Input>
                    {validation.errors.arrondissement &&
                    validation.touched.arrondissement ? (
                      <FormFeedback type="invalid">
                        {validation.errors.arrondissement}
                      </FormFeedback>
                    ) : null}
                  </div>
                  <div className="mb-3">
                    <Label className="form-label" htmlFor="name">
                      {props.t("Nom du quartier")}
                    </Label>
                    <Input
                      type="text"
                      className="form"
                      placeholder={props.t("Entrer le nom du quartier")}
                      name="name"
                      value={validation.values.name}
                      onBlur={validation.handleBlur}
                      onChange={validation.handleChange}
                      required
                    />
                    {validation.errors.name && validation.touched.name ? (
                      <FormFeedback type="invalid">
                        {validation.errors.name}
                      </FormFeedback>
                    ) : null}
                  </div>
                </CardBody>
              </Card>
              <div className="text-end mb-3">
                <Link to="/geolocalisation-quartier" className="btn btn-primary me-2">
                  {props.t("Retour")}
                </Link>
                <button
                  type="submit"
                  className="btn btn-warning w-sm m-2"
                  disabled={createQuartierMutation.isPending}
                >
                  {createQuartierMutation.isPending
                    ? props.t("Chargement")
                    : props.t("Ajouter")}
                </button>
              </div>
            </Form>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

AddQuartier.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
};

export default withRouter(withTranslation()(AddQuartier));
